import React, { useState, useEffect } from 'react';
import {
    ExpandableSection, SpaceBetween, Box, Button, FormField, Input, Spinner, Alert
} from '@amzn/awsui-components-react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { createPreference, listPreference, updatePreference } from 'src/utilities/AppSyncPlusAPIs';
import appSettings from './app_settings';

const PREFERENCE_TYPE = 'WORKDOCS_UPLOAD_DEFAULTS';
const ASSOCIATION_ID_TYPE = 'APP';

/**
 * WorkdocsUploadDefaultsForm - Admin form to set generic defaults for workdocs
 * upload details used when creating FRISCO processes from the CorpReporting page.
 *
 * Defaults are persisted to the generic preference table and consumed by
 * CreateFriscoModal when building the custom_attributes payload.
 */
export default function WorkdocsUploadDefaultsForm() {
    const currentStage = useSelector((globalState) => globalState.auth.currentStage);
    const userId = useSelector((globalState) => globalState.auth.userId);
    const appId = appSettings[currentStage]?.appId;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [existingPreferenceId, setExistingPreferenceId] = useState(null);
    const [parseFailed, setParseFailed] = useState(false);

    // Form fields matching the workdocs upload custom_attributes in CreateFriscoModal
    const [workdocsFolderId, setWorkdocsFolderId] = useState('');
    const [folderPathPattern, setFolderPathPattern] = useState('');
    const [fileNameFormat, setFileNameFormat] = useState('');

    useEffect(() => {
        if (appId) {
            loadDefaults();
        }
    }, [currentStage, appId]);

    const loadDefaults = () => {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        setParseFailed(false);
        listPreference(appId, ASSOCIATION_ID_TYPE, PREFERENCE_TYPE,
            (response) => {
                const items = response?.data?.listPreference?.items || [];
                if (items.length > 0) {
                    const pref = items[0];
                    setExistingPreferenceId(pref.preferenceId);
                    try {
                        const info = typeof pref.information === 'string'
                            ? JSON.parse(pref.information)
                            : pref.information;
                        setWorkdocsFolderId(info?.workdocsFolderId || '');
                        setFolderPathPattern(info?.folderPathPattern || '');
                        setFileNameFormat(info?.fileNameFormat || '');
                    } catch (e) {
                        console.error('Error parsing workdocs defaults preference:', e);
                        setError('Failed to parse existing workdocs upload defaults. Please refresh; saving is disabled to avoid overwriting existing data.');
                        setParseFailed(true);
                    }
                } else {
                    // No existing preference - set built-in defaults for display
                    const appConfig = appSettings[currentStage] || {};
                    setWorkdocsFolderId(appConfig.friscoWorkdocsFolderId || '');
                    setFolderPathPattern('Accounting Central Downloads/{previous_quarter} - {year}/{alias}');
                    setFileNameFormat('[{process_name}][{process_label}][{wb_status}]{uploaded_file_name}');
                }
                setLoading(false);
            },
            () => {
                setError('Failed to load workdocs upload defaults');
                setLoading(false);
            }
        );
    };

    const handleSave = () => {
        setSaving(true);
        setError('');
        setSuccessMessage('');

        const information = {
            workdocsFolderId: workdocsFolderId.trim(),
            folderPathPattern: folderPathPattern.trim(),
            fileNameFormat: fileNameFormat.trim()
        };

        if (existingPreferenceId) {
            // Update existing preference
            updatePreference(
                existingPreferenceId,
                'Workdocs Upload Defaults',
                userId,
                information,
                () => {
                    setSaving(false);
                    setSuccessMessage('Defaults saved successfully');
                },
                () => {
                    setSaving(false);
                    setError('Failed to save workdocs upload defaults');
                }
            );
        } else {
            // Create new preference
            const preferenceId = uuidv4();
            createPreference(
                preferenceId,
                PREFERENCE_TYPE,
                'Workdocs Upload Defaults',
                appId,
                ASSOCIATION_ID_TYPE,
                userId,
                information,
                null, // no TTL
                () => {
                    setExistingPreferenceId(preferenceId);
                    setSaving(false);
                    setSuccessMessage('Defaults saved successfully');
                },
                () => {
                    setSaving(false);
                    setError('Failed to save workdocs upload defaults');
                }
            );
        }
    };

    return (
        <ExpandableSection
            variant="container"
            defaultExpanded={false}
            headerText="Workdocs Upload Defaults"
            headerActions={<Button iconName="refresh" variant="icon" onClick={loadDefaults} />}
        >
            <SpaceBetween direction="vertical" size="m">
                <span className="small_grey_text_italics">
                    Set generic defaults for workdocs upload details used when creating FRISCO deliverables from SEC Central
                </span>

                {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}
                {successMessage && <Alert type="success" dismissible onDismiss={() => setSuccessMessage('')}>{successMessage}</Alert>}

                {loading ? (
                    <Box><Spinner /> Loading defaults...</Box>
                ) : (
                    <>
                        <FormField
                            label="Workdocs Folder ID"
                            description="The WorkDocs folder ID where uploaded files will be stored"
                        >
                            <Input
                                value={workdocsFolderId}
                                onChange={({ detail }) => setWorkdocsFolderId(detail.value)}
                                placeholder="e.g. babc60ae12eafb7a0815c12d63fc116a..."
                                disabled={saving}
                            />
                        </FormField>

                        <FormField
                            label="Workdocs Subfolder Path"
                            description="Subfolder path pattern in WorkDocs. Available tokens: {previous_quarter}, {year}, {alias}, {process_name}, {current_quarter}, {month}"
                        >
                            <Input
                                value={folderPathPattern}
                                onChange={({ detail }) => setFolderPathPattern(detail.value)}
                                placeholder="e.g. Accounting Central Downloads/{previous_quarter} - {year}/{alias}"
                                disabled={saving}
                            />
                        </FormField>

                        <FormField
                            label="File Name Prefix"
                            description="File name format for uploaded documents. Available tokens: {process_name}, {process_label}, {wb_status}, {uploaded_file_name}, {alias}"
                        >
                            <Input
                                value={fileNameFormat}
                                onChange={({ detail }) => setFileNameFormat(detail.value)}
                                placeholder="e.g. [{process_name}][{process_label}][{wb_status}]{uploaded_file_name}"
                                disabled={saving}
                            />
                        </FormField>

                        <Box float="right">
                            <Button variant="primary" loading={saving} disabled={parseFailed} onClick={handleSave}>
                                Save Defaults
                            </Button>
                        </Box>
                    </>
                )}
            </SpaceBetween>
        </ExpandableSection>
    );
}

/**
 * Utility function to load workdocs upload defaults from the generic preference table.
 * Used by CreateFriscoModal to pull in saved defaults.
 *
 * @param {string} appId - The application ID
 * @param {string} currentStage - The current deployment stage
 * @returns {Promise<Object>} Resolved defaults object with workdocsFolderId, folderPathPattern, fileNameFormat
 */
export function loadWorkdocsUploadDefaults(appId, currentStage) {
    return new Promise((resolve) => {
        listPreference(appId, ASSOCIATION_ID_TYPE, PREFERENCE_TYPE,
            (response) => {
                const items = response?.data?.listPreference?.items || [];
                if (items.length > 0) {
                    try {
                        const info = typeof items[0].information === 'string'
                            ? JSON.parse(items[0].information)
                            : items[0].information;
                        resolve({
                            workdocsFolderId: info?.workdocsFolderId || '',
                            folderPathPattern: info?.folderPathPattern || '',
                            fileNameFormat: info?.fileNameFormat || ''
                        });
                        return;
                    } catch (e) {
                        console.error('Error parsing workdocs defaults preference:', e);
                    }
                }
                // Fallback to app_settings defaults
                const appConfig = appSettings[currentStage] || {};
                resolve({
                    workdocsFolderId: appConfig.friscoWorkdocsFolderId || '',
                    folderPathPattern: 'Accounting Central Downloads/{previous_quarter} - {year}/{alias}',
                    fileNameFormat: '[{process_name}][{process_label}][{wb_status}]{uploaded_file_name}'
                });
            },
            () => {
                // On failure, fallback to app_settings defaults
                const appConfig = appSettings[currentStage] || {};
                resolve({
                    workdocsFolderId: appConfig.friscoWorkdocsFolderId || '',
                    folderPathPattern: 'Accounting Central Downloads/{previous_quarter} - {year}/{alias}',
                    fileNameFormat: '[{process_name}][{process_label}][{wb_status}]{uploaded_file_name}'
                });
            }
        );
    });
}
